import { useMemo } from "react";
import { useCasters } from "../../hooks/useCasters";
import { IStaff, Localization, RoleKey } from "../../types/types";
import { useLocalization } from "../../hooks/useLocalization";
import { casterPhotoUrl, onCasterPhotoError } from "../../utils/casterPhoto";
import { CommonLinkButton } from "../shared/CommonLinkButton";

const descriptionLength = (caster: IStaff, locale: Localization): number => {
  const text = locale === Localization.Finnish ? caster.description_fin : caster.description_en;
  return (text ?? "").trim().length;
};

const sortCastersByBioLength = (casters: IStaff[], locale: Localization): IStaff[] =>
  [...casters].sort((a, b) => descriptionLength(b, locale) - descriptionLength(a, locale));

const CasterCard = ({ caster, localization }: { caster: IStaff; localization: Localization }) => {
  const { strings } = useLocalization();
  const role: RoleKey = caster.role;
  const roleStrings = strings.ABOUT.CASTERS.ROLE as Record<RoleKey, string>;
  const description =
    localization === Localization.Finnish ? caster.description_fin : caster.description_en;
  const photo = casterPhotoUrl(caster.name);
  const hasHandle = caster.handle !== "none" && caster.handle !== "";

  const photoImgClass =
    "block h-full w-full object-cover object-[center_top] transition-transform duration-[350ms] ease-in-out group-hover:scale-[1.04]";

  return (
    <article className="group flex h-full flex-col overflow-hidden surface-card surface-card--interactive">
      <div className="relative w-full flex-shrink-0 aspect-square overflow-hidden bg-white/[0.03]">
        {caster.link ? (
          <a
            href={caster.link}
            target="_blank"
            rel="noopener noreferrer"
            className="block h-full w-full"
          >
            <img
              loading="lazy"
              decoding="async"
              src={photo}
              alt={caster.name}
              width={400}
              height={400}
              onError={onCasterPhotoError}
              className={photoImgClass}
            />
          </a>
        ) : (
          <img
            loading="lazy"
            decoding="async"
            src={photo}
            alt={caster.name}
            width={400}
            height={400}
            onError={onCasterPhotoError}
            className={photoImgClass}
          />
        )}
      </div>

      <div className="flex flex-1 flex-col gap-[0.45rem] p-[1rem_1.05rem_1.1rem] max-md:p-[1rem_1.35rem_1.15rem]">
        <h3 className="m-0 font-shentox text-[0.95rem] font-bold tracking-[0.06em] leading-[1.3] text-[#eee] uppercase">
          {caster.name}
        </h3>
        <span className="self-start rounded-full border border-[rgba(28,152,217,0.28)] bg-[rgba(28,152,217,0.1)] px-[0.55rem] py-[0.18rem] font-jetbrains text-[0.68rem] font-medium tracking-[0.1em] text-[#5cc2ff] uppercase">
          {roleStrings[role]}
        </span>
        {description ? (
          <p className="mt-[0.35rem] mb-0 flex-1 font-jetbrains text-[0.86rem] leading-[1.55] text-[#9a9ab8]">
            {description}
          </p>
        ) : null}
        {hasHandle && caster.link ? (
          <CommonLinkButton
            text={caster.handle}
            className="mt-auto self-start px-3 py-1.5 text-[0.82rem]"
            link={caster.link}
            icon="icons/twitter-logo.png"
          />
        ) : null}
      </div>
    </article>
  );
};

export const Casters = () => {
  const casters = useCasters();
  const { strings, locale } = useLocalization();
  const copy = strings.ABOUT.CASTERS;
  const sortedCasters = useMemo(
    () => (casters?.length ? sortCastersByBioLength(casters, locale) : []),
    [casters, locale],
  );

  return (
    <section aria-labelledby="about-casters-title">
      <header className="about-section-header about-section-header-center">
        <span className="about-section-eyebrow">{copy.EYEBROW}</span>
        <h2 id="about-casters-title">{copy.TITLE}</h2>
        <p className="about-section-subtitle-wide">{copy.SUBTITLE}</p>
      </header>

      {sortedCasters.length > 0 ? (
        <div className="grid gap-[1.15rem] [grid-template-columns:repeat(auto-fill,minmax(16.5rem,1fr))] max-md:grid-cols-1 max-md:gap-[0.85rem]">
          {sortedCasters.map((caster) => (
            <CasterCard caster={caster} localization={locale} key={caster.id} />
          ))}
        </div>
      ) : (
        <p className="m-0 rounded-[var(--surface-card-radius)] border border-[var(--surface-card-border)] bg-[var(--surface-card-bg)] px-5 py-8 text-center font-jetbrains text-[#9a9ab8]">
          {copy.NO_CASTERS}
        </p>
      )}
    </section>
  );
};
